import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FLOW_TYPES = [
  { key: 'welcome', label: 'Welcome' },
  { key: 'post_purchase', label: 'Post-purchase' },
  { key: 'win_back', label: 'Win-back' },
  { key: 're_engagement', label: 'Re-engagement' },
];

const ABHEmailPage = () => {
  const [overview, setOverview] = useState(null);
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [genFlowType, setGenFlowType] = useState('welcome');
  const [generating, setGenerating] = useState(false);
  const [sequence, setSequence] = useState(null);
  const [genErr, setGenErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [ov, rc] = await Promise.all([
        axios.get(`${API}/abh/email/overview`),
        axios.get(`${API}/abh/email/recommendations`),
      ]);
      setOverview(ov.data);
      setRecs(rc.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unable to load email intelligence');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    setGenErr(null);
    setSequence(null);
    try {
      const res = await axios.post(`${API}/abh/email/generate-sequence`, {
        flow_type: genFlowType,
      });
      setSequence(res.data?.sequence);
    } catch (e) {
      setGenErr(e.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const configured = overview?.configured;
  const hasData = overview?.has_data;

  return (
    <div data-testid="abh-email-page" className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-white text-2xl font-semibold">ABH Email Intelligence</h1>
          <p className="text-slate-400 text-sm mt-1">
            Klaviyo performance · Cipher recommendations · AI-generated sequences.
          </p>
        </div>
        <button
          data-testid="abh-email-refresh"
          onClick={load}
          className="text-sm px-3 py-1.5 rounded bg-slate-800 text-slate-200 hover:bg-slate-700"
        >
          Refresh
        </button>
      </div>

      {err && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : !configured ? (
        <div
          data-testid="abh-email-empty-configured"
          className="bg-slate-900/60 border border-slate-800 rounded-xl p-8 text-center"
        >
          <p className="text-slate-300 font-medium">Klaviyo not connected.</p>
          <p className="text-slate-500 text-sm mt-1.5 max-w-xl mx-auto">
            Connect Klaviyo to unlock email intelligence. Add <code className="text-amber-400">KLAVIYO_API_KEY</code> to your environment and re-deploy.
          </p>
        </div>
      ) : !hasData ? (
        <div
          data-testid="abh-email-empty-nosync"
          className="bg-slate-900/60 border border-slate-800 rounded-xl p-8 text-center"
        >
          <p className="text-slate-300 font-medium">Klaviyo connected. Awaiting first sync.</p>
          <p className="text-slate-500 text-sm mt-1.5">
            The nightly job will pull campaigns, flows, and segments. Or trigger manually via the sync endpoint.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Top campaigns */}
          <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
            <h2 className="text-white text-base font-medium mb-3">Top campaigns</h2>
            <div className="overflow-x-auto">
              <table data-testid="abh-email-campaigns" className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-800">
                    <th className="py-1.5 px-2 text-left font-normal">Name</th>
                    <th className="py-1.5 px-2 text-left font-normal">Subject</th>
                    <th className="py-1.5 px-2 text-right font-normal">Open rate</th>
                    <th className="py-1.5 px-2 text-right font-normal">Click rate</th>
                    <th className="py-1.5 px-2 text-right font-normal">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {(overview.top_campaigns || []).map((c) => (
                    <tr key={c.campaign_id} className="border-b border-slate-800/50">
                      <td className="py-1.5 px-2 text-slate-200">{c.name}</td>
                      <td className="py-1.5 px-2 text-slate-400 text-xs">{c.subject_line}</td>
                      <td className="py-1.5 px-2 text-right text-slate-300">
                        {c.open_rate != null ? `${(c.open_rate * 100).toFixed(1)}%` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right text-slate-300">
                        {c.click_rate != null ? `${(c.click_rate * 100).toFixed(1)}%` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-right text-slate-300">
                        ${Number(c.revenue || 0).toFixed(0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top flows + segments side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
              <h2 className="text-white text-base font-medium mb-3">Top flows</h2>
              <ul data-testid="abh-email-flows" className="space-y-1.5">
                {(overview.top_flows || []).map((f) => (
                  <li key={f.flow_id} className="text-sm flex justify-between bg-slate-800/40 rounded px-3 py-1.5">
                    <span className="text-slate-200">{f.name}</span>
                    <span className="text-slate-400 text-xs">${Number(f.revenue || 0).toFixed(0)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
              <h2 className="text-white text-base font-medium mb-3">Segments</h2>
              <ul data-testid="abh-email-segments" className="space-y-1.5">
                {(overview.segments || []).map((s) => (
                  <li key={s.segment_id} className="text-sm flex justify-between bg-slate-800/40 rounded px-3 py-1.5">
                    <span className="text-slate-200">{s.name}</span>
                    <span className="text-slate-400 text-xs">{s.size ?? '—'}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Recommendations */}
      <div
        data-testid="abh-email-recommendations"
        className="bg-slate-900/40 border border-slate-800 rounded-xl p-5"
      >
        <h2 className="text-white text-base font-medium mb-3">Cipher recommendations</h2>
        {recs?.recommendations?.length ? (
          <ul className="space-y-2">
            {recs.recommendations.map((r, i) => (
              <li
                key={i}
                data-testid={`abh-email-rec-${i}`}
                className="bg-emerald-500/5 border border-emerald-500/30 rounded-lg px-3 py-2.5"
              >
                <p className="text-sm text-emerald-200">{r.recommendation}</p>
                {r.rationale && (
                  <p className="text-xs text-slate-400 mt-1">— {r.rationale}</p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-slate-500 text-sm italic">
            {recs?.empty_message || 'No recommendations yet. Connect Klaviyo and sync.'}
          </p>
        )}
      </div>

      {/* Generate sequence */}
      <div
        data-testid="abh-email-generate"
        className="bg-slate-900/40 border border-slate-800 rounded-xl p-5"
      >
        <h2 className="text-white text-base font-medium mb-3">Generate email sequence</h2>
        <div className="flex flex-wrap items-center gap-3">
          <select
            data-testid="abh-email-flow-type"
            value={genFlowType}
            onChange={(e) => setGenFlowType(e.target.value)}
            className="bg-slate-800 text-slate-200 text-sm rounded px-2 py-1.5 border border-slate-700"
          >
            {FLOW_TYPES.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
          <button
            data-testid="abh-email-generate-btn"
            onClick={generate}
            disabled={generating}
            className="text-sm px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40"
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>
        {genErr && <p className="text-sm text-red-400 mt-2">{genErr}</p>}

        {sequence?.emails?.length > 0 && (
          <div
            data-testid="abh-email-sequence-output"
            className="mt-5 space-y-3"
          >
            {sequence.emails.map((em, i) => (
              <div
                key={i}
                data-testid={`abh-email-output-${i}`}
                className="bg-slate-800/40 border border-slate-700 rounded-lg p-4"
              >
                <p className="text-xs text-slate-500">Email #{em.number || i + 1}</p>
                <p className="text-white font-medium mt-1">{em.subject_line}</p>
                <p className="text-xs text-slate-400 italic mt-0.5">Preview: {em.preview_text}</p>
                <pre className="mt-3 text-sm text-slate-200 whitespace-pre-wrap font-sans">{em.body}</pre>
                <p className="text-emerald-300 text-sm mt-2">CTA: {em.cta}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ABHEmailPage;
